import api, { unwrap } from './client';
import type { ErpModule } from '@/api/platform.api';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  NotificationChannelPreference,
  NotificationRequest,
  NotificationResponse,
  UserNotificationPreferences,
} from '@/types/notification';

function normalizeChannelPreference(raw: unknown): NotificationChannelPreference {
  if (!raw || typeof raw !== 'object') {
    return { inApp: true, email: true };
  }
  const record = raw as Record<string, unknown>;
  return {
    inApp: record.inApp !== false && record.in_app !== false,
    email: record.email !== false,
  };
}

export function normalizeUserNotificationPreferences(raw: unknown): UserNotificationPreferences {
  const record = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const categoriesRaw = Array.isArray(record.categories) ? record.categories : [];
  const categories = categoriesRaw
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item) => ({
      code: String(item.code ?? ''),
      label: String(item.label ?? item.code ?? 'Notification'),
      description: String(item.description ?? ''),
      group:
        item.group === 'module'
          ? ('module' as const)
          : item.group === 'general'
            ? ('general' as const)
            : undefined,
      supportsInApp: item.supportsInApp !== false && item.supports_in_app !== false,
      supportsEmail: item.supportsEmail !== false && item.supports_email !== false,
    }))
    .filter((category) => category.code.length > 0);

  const prefsRaw =
    record.preferences && typeof record.preferences === 'object' && !Array.isArray(record.preferences)
      ? (record.preferences as Record<string, unknown>)
      : {};

  const preferences: Record<string, NotificationChannelPreference> = {};
  for (const [code, value] of Object.entries(prefsRaw)) {
    if (!code) continue;
    preferences[code] = normalizeChannelPreference(value);
  }

  return { categories, preferences };
}

export type NotificationListParams = SearchParams & { module?: ErpModule };

export const notificationsApi = {
  list: (params?: NotificationListParams) =>
    unwrap(api.get<ApiResponse<PageResponse<NotificationResponse>>>('/v1/notifications', { params })),

  unreadCount: (module?: ErpModule) =>
    unwrap(
      api.get<ApiResponse<{ count: number }>>('/v1/notifications/unread-count', {
        params: module ? { module } : undefined,
      }),
    ),

  create: (body: NotificationRequest) =>
    unwrap(api.post<ApiResponse<NotificationResponse>>('/v1/notifications', body)),

  markRead: (id: number) =>
    unwrap(api.patch<ApiResponse<NotificationResponse>>(`/v1/notifications/${id}/read`)),

  markAllRead: (module?: ErpModule) =>
    unwrap(
      api.patch<ApiResponse<{ updated: number }>>('/v1/notifications/read-all', null, {
        params: module ? { module } : undefined,
      }),
    ),

  clearRead: (module?: ErpModule) =>
    unwrap(
      api.delete<ApiResponse<{ cleared: number }>>('/v1/notifications/clear-read', {
        params: module ? { module } : undefined,
      }),
    ),

  clearAll: (module?: ErpModule) =>
    unwrap(
      api.delete<ApiResponse<{ cleared: number }>>('/v1/notifications/clear-all', {
        params: module ? { module } : undefined,
      }),
    ),

  preferences: async () =>
    normalizeUserNotificationPreferences(
      await unwrap(api.get<ApiResponse<UserNotificationPreferences>>('/v1/notifications/preferences')),
    ),

  updatePreferences: async (preferences: UserNotificationPreferences['preferences']) =>
    normalizeUserNotificationPreferences(
      await unwrap(
        api.put<ApiResponse<UserNotificationPreferences>>('/v1/notifications/preferences', {
          preferences,
        }),
      ),
    ),
};
